import { useNavigate } from '@tanstack/react-router';
import { ChevronsUpDown, Languages, LifeBuoy, LogOut, Settings, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@shared/auth/use-auth';
import { LanguageModal } from '@shared/components/layout/LanguageModal';
import { WorkspaceSwitcher } from '@shared/components/layout/WorkspaceSwitcher';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/components/ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@shared/components/ui/DropdownMenu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useHoverExpandLock,
  useSidebar,
} from '@shared/components/ui/sidebar';
import { useIsFamilyOffice } from '@shared/hooks/use-is-family-office';
import { useSectionAccess } from '@shared/hooks/use-section-access';
import { SECTION_CODES } from '@shared/lib/rbac-sections';
import { useSupportDialogStore } from '@shared/stores/support-dialog.store';

export function SidebarUserMenu() {
  const { user, logout } = useAuth();
  const { isMobile } = useSidebar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const companyAccess = useSectionAccess(SECTION_CODES.platformCustomization);
  const isFamilyOffice = useIsFamilyOffice();
  const openSupport = useSupportDialogStore((s) => s.openSupport);

  useHoverExpandLock(menuOpen);

  const initials = user ? `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase() : '?';
  const fullName = user ? `${user.firstName} ${user.lastName}` : '—';
  const email = user?.email ?? '';

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                tooltip={fullName}
                data-user-menu-trigger=""
                className="data-[state=open]:bg-sidebar-accent"
              >
                <Avatar className="size-8 shrink-0 rounded-lg">
                  {user?.avatar && <AvatarImage src={user.avatar} alt={user.firstName} />}
                  <AvatarFallback className="rounded-lg bg-muted text-[11px] font-medium text-muted-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-start leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate text-sm font-semibold text-sidebar-foreground">
                    {fullName}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">{email}</span>
                </div>
                <ChevronsUpDown className="ms-auto size-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="w-56 rounded-xl"
              side={isMobile ? 'bottom' : 'top'}
              align="end"
              sideOffset={8}
            >
              {/* Email */}
              <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                {email}
              </DropdownMenuLabel>

              <WorkspaceSwitcher />

              <DropdownMenuSeparator />

              {/* Settings → submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="gap-2">
                  <Settings className="size-3.5 text-muted-foreground" />
                  {t('sidebar.settings', 'Settings')}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-44 rounded-xl" sideOffset={8}>
                  <DropdownMenuItem
                    onClick={() => {
                      void navigate({ to: '/settings/profile' });
                    }}
                  >
                    {t('nav.settingsProfile', 'Profile')}
                  </DropdownMenuItem>
                  {companyAccess.canRead && (
                    <DropdownMenuItem
                      onClick={() => {
                        void navigate({ to: '/settings/company' });
                      }}
                    >
                      {t('nav.settingsCompany', 'Company')}
                    </DropdownMenuItem>
                  )}
                  {/* Support modal (DEV-4583) — Family Office users only. The dialog itself is
                      mounted at the app root by the support module. */}
                  {isFamilyOffice && (
                    <DropdownMenuItem
                      onClick={() => {
                        openSupport('settings_menu');
                      }}
                    >
                      {t('sidebar.support', 'Support')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              {/* Language */}
              <DropdownMenuItem
                className="gap-2"
                onClick={() => {
                  setLangOpen(true);
                }}
              >
                <Languages className="size-3.5 text-muted-foreground" />
                {t('sidebar.language', 'Language')}
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* AI Customization → the settings page */}
              <DropdownMenuItem
                className="gap-2"
                onClick={() => {
                  void navigate({ to: '/settings/ai-customization' });
                }}
              >
                <Users className="size-3.5 text-muted-foreground" />
                {t('sidebar.aiCustomization', 'AI Customization')}
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* Reset cache */}
              <DropdownMenuItem
                className="gap-2"
                onClick={() => {
                  window.location.reload();
                }}
              >
                <LifeBuoy className="size-3.5 text-muted-foreground" />
                {t('sidebar.resetCache', 'Reset cache')}
              </DropdownMenuItem>

              {/* Log out */}
              <DropdownMenuItem
                className="gap-2"
                onClick={() => {
                  void logout();
                }}
              >
                <LogOut className="size-3.5 text-muted-foreground" />
                {t('sidebar.logout', 'Log out')}
                <span className="ms-auto text-xs text-muted-foreground">⇧⌘L</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <LanguageModal open={langOpen} onOpenChange={setLangOpen} />
    </>
  );
}
